/* eslint-disable react-doctor/no-inline-exhaustive-style */
"use client";

import Link from "next/link";
import type { Sound } from "@/lib/supabase";
import { TPlay } from "@/components/ui/tls/t-play";
import { categoryBrowseHref } from "@/lib/category-data";
import { cleanSoundTitle } from "@/lib/utils";
import { TESLA_REQUIREMENTS } from "@/lib/audio-utils";
import { soundUrl } from "@/lib/slugify";
import { rowMetaTokens } from "./card-actions";

export interface StaticSoundRowProps {
  sound: Sound;
  showCategory?: boolean;
  /** §04.1 density axis, mirrored from SoundCard so the geometry matches. */
  density?: "d2";
}

/**
 * The resting SoundCard row, rendered without its hook stack.
 *
 * Windowed hydration (sort-grid.tsx, sound-grid.tsx) keeps every slot past
 * the eager window from mounting a full SoundCard until it scrolls near the
 * viewport, because 200 cards' worth of audio / favourites / tracking hooks on
 * one post-load task was the dominant mobile TBT cost (TES-834, TES-1077).
 * The first version of that window rendered an EMPTY hairline band for the
 * deferred slots, which cost the server HTML every row past the sixth: a
 * collection page with 164 sounds shipped 6 links, and in dark theme the
 * first paint read as "six sounds, then a stack of empty rules".
 *
 * This is the same row — same `.sc-*` classes, same grid areas, same 68px /
 * 76px geometry — carrying the content that does not need a hook: the title
 * as a real link, the category eyebrow, the meta run, and the resting
 * control outlines. A crawler and a first paint see every row; the upgrade
 * to a live SoundCard on intersection swaps like for like, so it causes no
 * layout shift.
 *
 * Server render and first client render agree by construction, which is why
 * this exists rather than a `typeof IntersectionObserver` initialiser: that
 * made the server paint a full card and the client paint a placeholder, a
 * hydration mismatch on every deferred row.
 *
 * The transport and action outlines are decorative (`aria-hidden`): the row
 * upgrades 400px before it reaches the viewport, so nobody is asked to press
 * a control that is not yet wired.
 */
export function StaticSoundRow({ sound, showCategory = true, density }: StaticSoundRowProps) {
  const titleId = `sound-row-title-${sound.id}`;
  const needsTrimming = (sound.duration || 0) > TESLA_REQUIREMENTS.MAX_DURATION;
  const tokens = rowMetaTokens({
    downloads: sound.downloads ?? 0,
    duration: sound.duration,
    needsTrimming,
  });

  return (
    <article
      aria-labelledby={titleId}
      data-testid="sound-card"
      data-hydration="deferred"
      data-row-density={density}
      className="sc-marquee"
    >
      {/* `.sc-band > :first-child` is hidden by the row CSS (the waveform
          slot); the empty span keeps the play control in the same child
          position as the live row. */}
      <div className="sc-band" aria-hidden="true">
        <span />
        <TPlay decorative playing={false} loaded={false} size="css" className="sc-play" />
      </div>

      <div className="sc-content">
        <div className="sc-eyebrow">
          {showCategory && sound.category && (
            <Link prefetch={false}
              href={categoryBrowseHref(sound.category)}
              // Effective target is 68x30, not 68x44 — the grown height
              // overflows the eyebrow track and paints under its neighbours.
              // That clears WCAG 2.5.8 AA (24x24); see `sound-card.tsx` for
              // why it is not pushed the rest of the way to 44.
              style={{
                textDecoration: "none",
                minWidth: 0,
                maxWidth: "100%",
                display: "inline-flex",
                alignItems: "center",
                minHeight: "var(--touch-target-min, 44px)",
                marginBlock: -15,
              }}
            >
              <span
                className="sc-category"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  minWidth: 0,
                  fontFamily: "var(--tls-font-body)",
                  fontSize: "0.6875rem",
                  fontWeight: 700,
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  color: "var(--tls-fg-muted)",
                }}
              >
                <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {sound.category}
                </span>
              </span>
            </Link>
          )}
        </div>

        <Link
          href={soundUrl(sound)}
          className="sc-title-link relative before:absolute before:inset-x-0 before:content-[''] sm:before:-inset-y-2"
          style={{ textDecoration: "none", display: "block", minWidth: 0 }}
        >
          <h3
            id={titleId}
            className="sc-title"
            style={{
              margin: 0,
              fontFamily: "var(--tls-font-display)",
              color: "var(--tls-fg-strong)",
            }}
          >
            {cleanSoundTitle(sound.title)}
          </h3>
        </Link>

        <span className="sc-run">
          {tokens.map((token, i) => (
            <span key={`${token}-${i}`}>
              {token}
              {i < tokens.length - 1 && (
                <span aria-hidden="true" className="sc-run-sep">
                  {" · "}
                </span>
              )}
            </span>
          ))}
        </span>

        {/* Resting outlines of the download and save controls, so the title
            column is the same width before and after the upgrade. */}
        <div className="sc-card-actions" aria-hidden="true">
          <span className="sc-download" style={STATIC_CONTROL_STYLE} />
          <span className="sc-save" data-keep="" style={STATIC_CONTROL_STYLE} />
        </div>
      </div>
    </article>
  );
}

const STATIC_CONTROL_STYLE: React.CSSProperties = {
  display: "inline-flex",
  width: 44,
  height: 44,
  minWidth: 44,
  minHeight: 44,
  flexShrink: 0,
  borderRadius: "var(--ls-radius-round)",
  border: "1px solid var(--tls-border-subtle)",
};
